import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import PageHero from 'components/PageHero';
import { BookOpen } from 'lucide-react';
import { getVariantRules, normalizeVariantRulesKey } from 'lib/variant-rules';
import { readLastVariant } from 'lib/variant-assets';
import { variantTheme } from 'lib/format-variant';

export default function Rules() {
  const [searchParams] = useSearchParams();
  const variantCode = searchParams.get('variant') || readLastVariant();
  const rules = useMemo(() => getVariantRules(variantCode), [variantCode]);
  const theme = variantTheme(variantCode);

  return (
    <div className={`rules-page rules-page--${theme} mx-auto w-full max-w-3xl pb-2`}>
      <PageHero
        eyebrow="Game rules"
        title={rules.title}
        showBack
        description={rules.description}
        icon={<BookOpen size={16} className="text-tp-gold" />}
      />

      <section className="glass-panel mt-6 p-6">
        <h2 className="text-lg font-semibold text-white">Objective</h2>
        <p className="mt-2 text-sm leading-relaxed text-white/80">{rules.objective}</p>
      </section>

      {rules.extraSections.map((section) => (
        <section key={section.title} className="glass-panel mt-4 p-6">
          <h2 className="text-lg font-semibold text-white">{section.title}</h2>
          {Array.isArray(section.body) ? (
            <ul className="mt-3 list-inside list-disc space-y-2 text-sm text-white/80">
              {section.body.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          ) : (
            <p className="mt-2 text-sm leading-relaxed text-white/80">{section.body}</p>
          )}
        </section>
      ))}

      <section className="glass-panel mt-4 p-6">
        <h2 className="text-lg font-semibold text-white">
          Hand Rankings ({normalizeVariantRulesKey(variantCode) === 'muflis' ? 'Strongest → Weakest in Muflis' : 'High → Low'})
        </h2>
        {rules.handRankingNote && (
          <p className="mt-2 text-sm text-white/60">{rules.handRankingNote}</p>
        )}
        <div className="mt-4 space-y-2">
          {rules.handRankings.map((h) => (
            <div key={h.rank} className="flex items-center justify-between rounded-lg bg-white/5 px-4 py-2 text-sm">
              <span>
                <span className="mr-2 text-casino-gold">{h.rank}.</span>
                {h.name}
              </span>
              <span className="font-mono text-white/60">{h.example}</span>
            </div>
          ))}
        </div>
      </section>

      {rules.examples && rules.examples.length > 0 && (
        <section className="glass-panel mt-4 p-6">
          <h2 className="text-lg font-semibold text-white">Examples</h2>
          <div className="mt-4 space-y-3">
            {rules.examples.map((ex) => (
              <div key={ex.label} className="rounded-lg border border-white/10 bg-white/[0.03] px-4 py-3 text-sm">
                <p className="font-semibold text-tp-gold">{ex.label}</p>
                <p className="mt-1 text-white/75">
                  <span className="text-white/45">Cards: </span>
                  <span className="font-mono">{ex.original}</span>
                </p>
                <p className="mt-1 text-white/75">
                  <span className="text-white/45">Result: </span>
                  <span className="font-mono">{ex.result}</span>
                </p>
                {ex.note && <p className="mt-1 text-xs text-white/50">{ex.note}</p>}
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
